"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { useTranslations } from "@zapier/i18n";

import {
  Button,
  IconButton,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Tooltip,
} from "@zapier/design-system";

import {
  HydratedAsset,
  useCopyAssetsToAccount,
  useListAssetsInFolderSubtree,
  useListAssetsInFolderSubtrees,
  useListFolderSubtreeMaxDepth,
  useSplitTreatments,
} from "@zapier/client";
import { isPersonalFolder, useNotification } from "@zapier/utils";

import { useRouterParams } from "../../../hooks/useRouterParams";
import {
  CopyToAnotherAccountEntryPoint,
  useEmitCopyToAnotherAccountEvents,
} from "../../../events/useEmitCopyToAnotherAccountEvents";
import { ButtonWrapper } from "../../common/ButtonWrapper";
import { FolderPickerModal } from "../FolderPickerModal";
import { RedirectLoadingOverlay } from "../RedirectLoadingOverlay";
import { AccountPickerContent } from "./AccountPickerContent";
import { ReviewAndConfirmContent } from "./ReviewAndConfirmContent";
import { stashConnectHandoff } from "../ConnectAppsModal/connectHandoff";
import {
  buildCombinedPreviewAssets,
  buildCopyToAnotherAccountPayload,
  buildTypedIds,
  hasTableAssets,
  MAX_SUBFOLDER_DEPTH,
  resolveCopyRedirectPath,
  shouldShowTopLevelFolderOption,
  type CreateFolderDepthDisabledReason,
} from "./copyToAnotherAccountUtils";
import styles from "./CopyToAnotherAccountModal.module.css";

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  assets: HydratedAsset[];
  copyEntryPoint?: CopyToAnotherAccountEntryPoint;
  /** Optional source folders; each folder is copied as a full subtree. */
  folderIds?: string[];
  /** True when current selection contains only folder items (no loose assets). */
  selectedItemsAreAllFolders?: boolean;
}

type Step =
  | { kind: "account" }
  | { kind: "folder"; targetAccountId: string; targetAccountName: string }
  | {
      kind: "review";
      targetAccountId: string;
      targetAccountName: string;
      destinationFolderId: string | null;
      destinationFolderName?: string;
    }
  | {
      kind: "redirecting";
      targetAccountId: string;
      targetAccountName: string;
      destinationFolderId: string | null;
    };

const ACCOUNT_STEP: Step = { kind: "account" };

/**
 * Single-modal multi-step orchestrator for the cross-account copy flow.
 *
 * One `ModalDialog.Root` whose header/body/footer swap based on `step`:
 *   account → folder → review → (success: redirecting overlay; error: stay on review)
 *
 * State resets to the account step whenever the modal closes so reopening
 * starts fresh.
 */
export function CopyToAnotherAccountModal(props: Props) {
  const {
    assets,
    isOpen,
    copyEntryPoint = "unknown",
    folderIds,
    selectedItemsAreAllFolders,
  } = props;
  const t = useTranslations("copy-to-another-account-modal");
  const parameters = useParams();
  const { memberId } = useRouterParams();
  const { createNotification } = useNotification();
  const { copyAssetsToAccount, isCopying } = useCopyAssetsToAccount();
  const { emit } = useEmitCopyToAnotherAccountEvents();
  const normalizedFolderIds = folderIds?.filter((id) => id.length > 0) ?? [];
  const singleFolderId =
    normalizedFolderIds.length === 1 ? normalizedFolderIds[0] : undefined;
  const ownerIdForSubtreePreview =
    isPersonalFolder(String(parameters?.folder)) && memberId
      ? memberId
      : undefined;
  const sourceFolderIds = normalizedFolderIds;

  // These subtree preview queries fan out to expensive org-service calls
  // (`/v0/tree` + `/v2/folders/{id}/assets` per subfolder). The modal mounts
  // closed on every folder page, so gate the queries on the modal being open
  // (and the feature flag) to avoid firing them on plain folder browsing
  // (ASM-4337). `refreshReviewPreview()` lazily loads them on review entry.
  const { isDuplicateAssetsEnabled, isCopyTableRecordsEnabled } =
    useSplitTreatments();
  const previewActive = isOpen && isDuplicateAssetsEnabled;

  // For folder-tree copies, the prop `assets` only includes the *direct*
  // assets of the source folder (from `useFolderAssets`). zsl-api walks the
  // subtree and copies nested assets too, so we fetch the full recursive
  // list here for an accurate review-step preview. Asset-only copies
  // (no folderIds) skip this query.
  const {
    assets: subtreeAssets,
    isLoading: subtreePreviewLoading,
    refetch: refetchSubtreePreview,
  } = useListAssetsInFolderSubtree(
    previewActive ? singleFolderId : undefined,
    ownerIdForSubtreePreview
      ? { ownerId: ownerIdForSubtreePreview }
      : undefined,
  );
  const {
    assets: subtreeAssetsMulti,
    isLoading: subtreeMultiPreviewLoading,
    refetchAll: refetchSubtreeMultiPreview,
  } = useListAssetsInFolderSubtrees(
    previewActive && normalizedFolderIds.length > 1
      ? normalizedFolderIds
      : undefined,
    ownerIdForSubtreePreview
      ? { ownerId: ownerIdForSubtreePreview }
      : undefined,
  );
  const { maxDepth: sourceSubtreeDepth } = useListFolderSubtreeMaxDepth(
    previewActive && sourceFolderIds.length > 0 ? sourceFolderIds : undefined,
    ownerIdForSubtreePreview
      ? { ownerId: ownerIdForSubtreePreview }
      : undefined,
  );
  const sourceSubtreeMaxDepth =
    sourceSubtreeDepth !== undefined && sourceSubtreeDepth >= 1
      ? sourceSubtreeDepth
      : undefined;
  const { hasFolderSource, areSelectedItemsAllFolders, combinedPreviewAssets } =
    buildCombinedPreviewAssets({
      assets,
      subtreeAssets,
      subtreeAssetsMulti,
      folderIds: normalizedFolderIds,
      selectedItemsAreAllFolders,
    });

  const [step, setStep] = useState<Step>(ACCOUNT_STEP);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    // eslint-disable-next-line unicorn/no-null
    null,
  );
  const [selectedAccountName, setSelectedAccountName] = useState<string>("");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    // eslint-disable-next-line unicorn/no-null
    null,
  );
  const [selectedFolderName, setSelectedFolderName] = useState<
    string | undefined
  >();
  const [copyAsTopLevelFolder, setCopyAsTopLevelFolder] = useState(false);
  const [folderHasPendingNew, setFolderHasPendingNew] = useState(false);
  const [folderIsMutating, setFolderIsMutating] = useState(false);
  const [isSelectedFolderDepthAllowed, setIsSelectedFolderDepthAllowed] =
    useState(true);
  const [createDisabledDepthReason, setCreateDisabledDepthReason] = useState<
    CreateFolderDepthDisabledReason | undefined
  >();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  // Yes/No selection for "Copy table records?". `undefined` = no choice yet,
  // which keeps the Copy button disabled when the copy includes a table.
  const [includeTableRecords, setIncludeTableRecords] = useState<
    boolean | undefined
  >();
  const startNewFolderReference = useRef<(() => void) | undefined>();
  const hasEmittedOpenModalReference = useRef(false);
  const typedIds = buildTypedIds(assets);
  // Use the full preview list so tables nested inside copied folders count.
  const copyIncludesTables = useMemo(
    () => hasTableAssets(combinedPreviewAssets),
    [combinedPreviewAssets],
  );
  // The "Copy table records?" choice is gated behind a feature flag and only
  // surfaces when the copy actually includes a table.
  const showTableRecordsChoice =
    isCopyTableRecordsEnabled && copyIncludesTables;

  const resetState = useCallback(() => {
    setStep(ACCOUNT_STEP);
    // eslint-disable-next-line unicorn/no-null
    setSelectedAccountId(null);
    setSelectedAccountName("");
    // eslint-disable-next-line unicorn/no-null
    setSelectedFolderId(null);
    setSelectedFolderName(undefined);
    setCopyAsTopLevelFolder(false);
    setFolderHasPendingNew(false);
    setFolderIsMutating(false);
    setIsSelectedFolderDepthAllowed(true);
    setCreateDisabledDepthReason(undefined);
    setErrorMessage(undefined);
    setIncludeTableRecords(undefined);
    startNewFolderReference.current = undefined;
  }, []);

  const handleTargetFoldersError = useCallback(
    (reason: string) => {
      emit({ action: "blocked", copyEntryPoint, blockedReason: reason });
    },
    [emit, copyEntryPoint],
  );

  const refreshReviewPreview = useCallback(async () => {
    if (!hasFolderSource) return;

    await Promise.all([
      singleFolderId ? refetchSubtreePreview() : Promise.resolve(),
      normalizedFolderIds.length > 1
        ? refetchSubtreeMultiPreview()
        : Promise.resolve(),
    ]);
  }, [
    hasFolderSource,
    normalizedFolderIds.length,
    refetchSubtreeMultiPreview,
    refetchSubtreePreview,
    singleFolderId,
  ]);

  // When the modal closes externally, reset so reopening is clean.
  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen, resetState]);

  useEffect(() => {
    if (!isOpen) {
      hasEmittedOpenModalReference.current = false;
      return;
    }
    if (hasEmittedOpenModalReference.current) return;
    hasEmittedOpenModalReference.current = true;
    emit({ action: "open_modal", copyEntryPoint });
  }, [copyEntryPoint, emit, isOpen]);

  useEffect(() => {
    if (step.kind === "folder") return;
    setCreateDisabledDepthReason(undefined);
    setIsSelectedFolderDepthAllowed(true);
  }, [step.kind]);

  if (!isOpen) return;

  // Once the success path triggers, the modal is dismissed and only the
  // overlay renders until `location.href` navigates the page.
  if (step.kind === "redirecting") {
    return (
      <RedirectLoadingOverlay targetAccountName={step.targetAccountName} />
    );
  }

  const handleClose = () => {
    if (isCopying) return; // ignore close attempts mid-copy
    props.setIsOpen(false);
  };

  const handleAccountContinue = () => {
    if (!selectedAccountId) return;
    if (!hasFolderSource) {
      const hasAssets =
        typedIds.zapIds.length > 0 ||
        typedIds.interfaceIds.length > 0 ||
        typedIds.tableIds.length > 0 ||
        typedIds.chatbotIds.length > 0 ||
        typedIds.agentIds.length > 0 ||
        typedIds.canvasIds.length > 0;
      if (!hasAssets) {
        createNotification({
          message: t("notifications.no-assets"),
          type: "warning",
        });
        return;
      }
    }
    setStep({
      kind: "folder",
      targetAccountId: selectedAccountId,
      targetAccountName: selectedAccountName,
    });
  };

  const handleFolderContinue = () => {
    const hasInvalidSelectedDestination =
      !!selectedFolderId &&
      !copyAsTopLevelFolder &&
      !isSelectedFolderDepthAllowed;
    if (
      (!selectedFolderId && !copyAsTopLevelFolder) ||
      hasInvalidSelectedDestination ||
      step.kind !== "folder"
    )
      return;
    setStep({
      kind: "review",
      targetAccountId: step.targetAccountId,
      targetAccountName: step.targetAccountName,
      destinationFolderId: selectedFolderId,
      destinationFolderName: copyAsTopLevelFolder
        ? undefined
        : selectedFolderName,
    });
    void refreshReviewPreview();
  };

  const handleStartNewFolder = () => {
    startNewFolderReference.current?.();
  };

  const handleCopy = async () => {
    if (step.kind !== "review") return;
    const assetsToBeCopiedCount = combinedPreviewAssets.length;
    setErrorMessage(undefined);
    emit({
      action: "submit",
      copyEntryPoint,
      assetsToBeCopiedCount,
      // Track the table-records choice (only set when the control was shown)
      // so we can measure opt-in vs opt-out rates.
      ...(showTableRecordsChoice ? { includeTableRecords } : {}),
    });
    try {
      const requestPayload = buildCopyToAnotherAccountPayload({
        targetAccountId: step.targetAccountId,
        destinationFolderId: step.destinationFolderId,
        folderIds: normalizedFolderIds,
        typedIds,
        selectedItemsAreAllFolders: areSelectedItemsAllFolders,
        // Only forward the flag when the choice was surfaced to the user.
        includeTableRecords: showTableRecordsChoice
          ? includeTableRecords
          : undefined,
      });
      const copyResult = await copyAssetsToAccount(requestPayload);
      // zsl-api can include folder records in created/failed results; analytics
      // "asset" counts should align to the non-folder preview in this modal.
      const assetsCopiedCount = copyResult.created.filter(
        (item) => item.type.toLowerCase() !== "folder",
      ).length;
      const assetsFailedCount = copyResult.failed.filter(
        (item) => item.type.toLowerCase() !== "folder",
      ).length;
      const action =
        assetsCopiedCount === 0 && assetsFailedCount > 0
          ? "failure"
          : assetsFailedCount > 0
            ? "partial_success"
            : "success";
      emit({
        action,
        copyEntryPoint,
        assetsToBeCopiedCount,
        assetsCopiedCount,
        assetsFailedCount,
      });

      // Copied assets land without app connections. Hand the apps that need
      // connecting to the destination folders page via sessionStorage — the
      // redirect below unmounts the app, so in-memory state can't survive.
      stashConnectHandoff({
        targetAccountId: step.targetAccountId,
        destinationFolderId: step.destinationFolderId,
        connectionsNeeded: copyResult.connectionsNeeded ?? [],
      });

      // Switch to overlay then navigate. Setting location.href here unmounts
      // the entire app; the overlay covers the brief gap before navigation.
      setStep({
        kind: "redirecting",
        targetAccountId: step.targetAccountId,
        targetAccountName: step.targetAccountName,
        destinationFolderId: step.destinationFolderId,
      });
      const next = resolveCopyRedirectPath({
        destinationFolderId: step.destinationFolderId,
      });
      const switchUrl = `/switch-account?account=${encodeURIComponent(step.targetAccountId)}&next=${encodeURIComponent(next)}`;
      globalThis.location.href = switchUrl;
    } catch (error) {
      console.error("Failed to copy assets to account:", error);
      emit({
        action: "failure",
        copyEntryPoint,
        assetsToBeCopiedCount,
      });
      setErrorMessage(
        error instanceof Error ? error.message : t("notifications.copy-failed"),
      );
    }
  };

  /**
   * Step-back handler. Account is the first step so it has no back; the
   * redirecting step is post-success (modal already gone). Folder→account
   * just resets the step. Review→folder preserves target context.
   */
  const handleBack = () => {
    if (step.kind === "folder") {
      setStep(ACCOUNT_STEP);
      return;
    }
    if (step.kind === "review") {
      setStep({
        kind: "folder",
        targetAccountId: step.targetAccountId,
        targetAccountName: step.targetAccountName,
      });
      // Clear any error from the previous review attempt — it's tied to that
      // attempt, not the user's selections.
      setErrorMessage(undefined);
    }
  };

  const canGoBack =
    (step.kind === "folder" || step.kind === "review") && !isCopying;

  const isCreateNewFolderDepthDisabled =
    createDisabledDepthReason !== undefined;
  const createNewFolderTooltip = (() => {
    if (!isCreateNewFolderDepthDisabled) return;
    if (createDisabledDepthReason === "copy-projection-depth") {
      return t("folder-step.create-disabled-copy-depth", {
        maxDepth: MAX_SUBFOLDER_DEPTH,
      });
    }
    return t("folder-step.create-disabled-max-depth", {
      maxDepth: MAX_SUBFOLDER_DEPTH,
    });
  })();

  // Per-step header / body / footer.
  const headerText = (() => {
    switch (step.kind) {
      case "account": {
        return t("account-step.title");
      }
      case "folder": {
        return t("folder-step.title");
      }
      case "review": {
        return t("review-step.title", { accountName: step.targetAccountName });
      }
    }
  })();

  const body = (() => {
    switch (step.kind) {
      case "account": {
        return (
          <AccountPickerContent
            selectedAccountId={selectedAccountId}
            onSelect={(id, name) => {
              if (id !== selectedAccountId) {
                // Prevent stale destination folder state from carrying to a new account.
                // eslint-disable-next-line unicorn/no-null
                setSelectedFolderId(null);
                setSelectedFolderName(undefined);
                setFolderHasPendingNew(false);
                setFolderIsMutating(false);
                setIsSelectedFolderDepthAllowed(true);
                setCreateDisabledDepthReason(undefined);
                setErrorMessage(undefined);
                startNewFolderReference.current = undefined;
              }
              setSelectedAccountId(id);
              setSelectedAccountName(name);
            }}
          />
        );
      }
      case "folder": {
        return (
          <FolderPickerModal
            targetAccountId={step.targetAccountId}
            selectedFolderId={selectedFolderId}
            onSelectedFolderIdChange={(folderId) => {
              setSelectedFolderId(folderId);
              if (folderId) setCopyAsTopLevelFolder(false);
            }}
            onSelectedFolderNameChange={setSelectedFolderName}
            showCopyAsTopLevelFolderOption={shouldShowTopLevelFolderOption({
              hasFolderSource,
              selectedItemsAreAllFolders: areSelectedItemsAllFolders,
            })}
            copyAsTopLevelFolder={copyAsTopLevelFolder}
            onCopyAsTopLevelFolderChange={(value) => {
              setCopyAsTopLevelFolder(value);
              if (value) {
                // eslint-disable-next-line unicorn/no-null
                setSelectedFolderId(null);
                setSelectedFolderName(undefined);
                setIsSelectedFolderDepthAllowed(true);
              }
            }}
            onMutatingChange={setFolderIsMutating}
            onPendingNewChange={setFolderHasPendingNew}
            onSelectedDestinationValidityChange={
              setIsSelectedFolderDepthAllowed
            }
            onCreateNewFolderDisabledChange={({ reason }) => {
              setCreateDisabledDepthReason(reason);
            }}
            registerStartNewFolder={(start) => {
              startNewFolderReference.current = start;
            }}
            sourceSubtreeMaxDepth={sourceSubtreeMaxDepth}
            hasFolderSource={hasFolderSource}
            onTargetFoldersError={handleTargetFoldersError}
          />
        );
      }
      case "review": {
        return (
          <ReviewAndConfirmContent
            targetAccountName={step.targetAccountName}
            destinationFolderName={step.destinationFolderName}
            isTopLevelDestination={step.destinationFolderId === null}
            assets={combinedPreviewAssets}
            isLoadingAssets={
              subtreePreviewLoading || subtreeMultiPreviewLoading
            }
            isCopying={isCopying}
            errorMessage={errorMessage}
            showTableRecordsChoice={showTableRecordsChoice}
            tableRecordsChoice={includeTableRecords}
            onTableRecordsChoiceChange={setIncludeTableRecords}
          />
        );
      }
    }
  })();

  const footer = (
    <div className={styles.footer}>
      {step.kind === "folder"
        ? (() => {
            const isCreateNewFolderDisabled =
              folderHasPendingNew ||
              folderIsMutating ||
              isCreateNewFolderDepthDisabled;
            const createButton = (
              <button
                type="button"
                className={styles.createButton}
                onClick={handleStartNewFolder}
                disabled={isCreateNewFolderDisabled}
              >
                {t("buttons.create-new-folder")}
              </button>
            );

            if (!createNewFolderTooltip) return createButton;
            return (
              <Tooltip allowMultiline content={createNewFolderTooltip}>
                <ButtonWrapper isDisabled={isCreateNewFolderDisabled}>
                  {createButton}
                </ButtonWrapper>
              </Tooltip>
            );
          })()
        : undefined}
      <div className={styles.footerActions}>
        <Button variant="tertiary" type="button" onPress={handleClose}>
          {t("buttons.cancel")}
        </Button>
        {(() => {
          switch (step.kind) {
            case "account": {
              return (
                <Button
                  type="button"
                  onPress={handleAccountContinue}
                  isDisabled={!selectedAccountId}
                >
                  {t("buttons.continue")}
                </Button>
              );
            }
            case "folder": {
              return (
                <Button
                  type="button"
                  onPress={handleFolderContinue}
                  isDisabled={
                    (!selectedFolderId && !copyAsTopLevelFolder) ||
                    folderIsMutating ||
                    folderHasPendingNew ||
                    (!!selectedFolderId &&
                      !copyAsTopLevelFolder &&
                      !isSelectedFolderDepthAllowed)
                  }
                >
                  {t("buttons.continue")}
                </Button>
              );
            }
            case "review": {
              return (
                <Button
                  type="button"
                  onPress={handleCopy}
                  isPending={isCopying}
                  isDisabled={
                    // Only gate the Copy button when the table-records choice
                    // is active. While the subtree preview is loading we don't
                    // yet know if tables are included, so block Copy to avoid
                    // bypassing the choice (folder copies with nested tables).
                    isCopyTableRecordsEnabled &&
                    (subtreePreviewLoading ||
                      subtreeMultiPreviewLoading ||
                      (copyIncludesTables && includeTableRecords === undefined))
                  }
                >
                  {t("buttons.copy-assets")}
                </Button>
              );
            }
          }
        })()}
      </div>
    </div>
  );

  return (
    <ModalDialogRoot
      width={650}
      isOpen={true}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <ModalDialogHeader textAlign="start">
        {canGoBack ? (
          <span className={styles.headerWithBack}>
            <IconButton
              aria-label="Go back"
              variant="ghost"
              name="arrowBigLeft"
              size="medium"
              onPress={handleBack}
            />
            <span>{headerText}</span>
          </span>
        ) : (
          headerText
        )}
      </ModalDialogHeader>
      <ModalDialogBody textAlign="start">
        <div className={styles.body}>{body}</div>
      </ModalDialogBody>
      <ModalDialogFooter>{footer}</ModalDialogFooter>
    </ModalDialogRoot>
  );
}
